import React, { useState, useEffect } from 'react';
import {
  validateAirport,
  validateFlightNumber,
  validateTime,
  validateTimeSequence,
  calculateBlockTime,
  calculateConnectionTime,
  validateFlight,
  getFieldClassName,
  hasConnectionWarning
} from '../utils/flightValidation';

/**
 * Flight Editor Modal Component
 * Allows editing individual flight legs with validation
 * 
 * Props:
 *   - isOpen: boolean - Modal visibility
 *   - flight: object - Current flight data
 *   - previousFlightIn: string - Previous flight's arrival time (for connection warnings)
 *   - nextFlightOut: string - Next flight's departure time (for connection warnings)
 *   - onClose: function - Callback when modal closes
 *   - onSave: function - Callback when changes are applied
 */
export default function FlightEditorModal({ 
  isOpen, 
  flight, 
  previousFlightIn = null,
  nextFlightOut = null,
  onClose, 
  onSave 
}) {
  
  // Form state - initialize from passed flight
  const [formData, setFormData] = useState({
    flightNumber: '',
    origin: '',
    destination: '',
    scheduledOut: '',
    scheduledIn: '',
    aircraft: '',
    isDeadhead: false
  });

  const [errors, setErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [connectionWarning, setConnectionWarning] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form when flight prop changes
  useEffect(() => {
    if (flight) {
      setFormData({
        flightNumber: flight.flight_number || '',
        origin: flight.origin || flight.departure_airport || '',
        destination: flight.destination || flight.arrival_airport || '',
        scheduledOut: flight.scheduled_out_time 
          ? formatTimeForInput(flight.scheduled_out_time) 
          : flight.scheduledOut || '',
        scheduledIn: flight.scheduled_in_time 
          ? formatTimeForInput(flight.scheduled_in_time) 
          : flight.scheduledIn || '',
        aircraft: flight.aircraft_model || flight.aircraft || '',
        isDeadhead: flight.is_deadhead || false
      });
      setErrors({});
      setShowValidation(false);
    }
  }, [flight, isOpen]);

  // Check connection times whenever times change
  useEffect(() => {
    if (previousFlightIn && formData.scheduledOut) {
      const timeValidation = validateTime(formData.scheduledOut);
      if (timeValidation.valid) {
        const warning = hasConnectionWarning(previousFlightIn, formData.scheduledOut);
        setConnectionWarning(warning.isWarning ? warning.message : null);
      }
    }
  }, [formData.scheduledOut, previousFlightIn]);

  /**
   * Format time string to HH:MM (handles various input formats)
   */
  const formatTimeForInput = (timeStr) => {
    if (!timeStr) return '';
    // If already HH:MM format, return as-is
    if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
    // If ISO format (2026-07-02T19:59:00), extract time
    if (timeStr.includes('T')) {
      return timeStr.split('T')[1].substring(0, 5);
    }
    return timeStr;
  };

  /**
   * Handle input field changes
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value.toUpperCase()
    }));

    // Clear error for this field as user corrects it
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  /**
   * Handle time input (HH:MM format)
   */
  const handleTimeInput = (e, field) => {
    const value = e.target.value;
    
    // Allow empty for now
    if (!value) {
      setFormData(prev => ({ ...prev, [field]: '' }));
      return;
    }

    // Allow typing digits and colon
    if (/^[0-9:]*$/.test(value) && value.length <= 5) {
      // Auto-format as user types: "19" → "19:", "1959" → "19:59"
      let formatted = value;
      if (value.length === 2 && !value.includes(':')) {
        formatted = value + ':';
      } else if (value.length === 5 && value.includes(':')) {
        formatted = value;
      }
      
      setFormData(prev => ({ ...prev, [field]: formatted }));
      
      // Clear error if valid
      if (formatted.length === 5) {
        const validation = validateTime(formatted);
        if (validation.valid && errors[field]) {
          setErrors(prev => ({ ...prev, [field]: null }));
        }
      }
    }
  };

  /**
   * Validate and save changes
   */
  const handleSave = async () => {
    setShowValidation(true);
    
    const validation = validateFlight(formData);
    
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    // Calculate block time for preview
    const blockTime = calculateBlockTime(formData.scheduledOut, formData.scheduledIn);

    setIsSaving(true);
    try {
      await onSave({
        ...flight,
        flight_number: formData.flightNumber,
        origin: formData.origin,
        destination: formData.destination,
        departure_airport: formData.origin,
        arrival_airport: formData.destination,
        scheduled_out_time: formData.scheduledOut,
        scheduled_in_time: formData.scheduledIn,
        aircraft_model: formData.aircraft,
        is_deadhead: formData.isDeadhead,
        blockTime: blockTime.timeString
      });
      
      onClose();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Calculate and display block time
   */
  const blockTimeDisplay = formData.scheduledOut && formData.scheduledIn
    ? calculateBlockTime(formData.scheduledOut, formData.scheduledIn).timeString
    : 'N/A';

  if (!isOpen || !flight) return null;

  return (
    <>
      {/* Backdrop - click to close */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center border-b border-blue-800">
            <div>
              <h2 className="text-xl font-bold text-white">
                Edit Flight {formData.flightNumber || 'N/A'}
              </h2>
              <p className="text-blue-100 text-sm">
                Leg #{flight.leg_sequence || 'N/A'} • {formData.origin || '?'} → {formData.destination || '?'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-blue-100 hover:text-white text-2xl font-light"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            
            {/* Submission Error */}
            {errors.submit && (
              <div className="p-4 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
                {errors.submit}
              </div>
            )}

            {/* Flight Identification Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">
                  ✈️
                </span>
                Flight Information
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Flight Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flight Number *
                  </label>
                  <input
                    type="text"
                    name="flightNumber"
                    value={formData.flightNumber}
                    onChange={handleInputChange}
                    placeholder="DL123"
                    maxLength="6"
                    className={`w-full px-3 py-2 border rounded-lg font-mono text-lg ${getFieldClassName('flightNumber', errors)} transition-colors`}
                  />
                  {errors.flightNumber && (
                    <p className="text-red-500 text-xs mt-1">{errors.flightNumber}</p>
                  )}
                </div>

                {/* Aircraft Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aircraft
                  </label>
                  <input
                    type="text"
                    name="aircraft"
                    value={formData.aircraft}
                    onChange={handleInputChange}
                    placeholder="739 or 73N"
                    maxLength="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-lg focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>

              {/* Deadhead Checkbox */}
              <label className="flex items-center mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isDeadhead"
                  checked={formData.isDeadhead}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                />
                <span className="ml-2 text-sm text-gray-700">This is a deadhead (passenger leg)</span>
              </label>
            </div>

            {/* Route Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <span className="bg-green-100 text-green-700 rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">
                  🗺️
                </span>
                Route
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Origin Airport */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From (Origin) *
                  </label>
                  <input
                    type="text"
                    name="origin"
                    value={formData.origin}
                    onChange={handleInputChange}
                    placeholder="JFK"
                    maxLength="3"
                    className={`w-full px-3 py-2 border rounded-lg font-mono text-xl font-bold ${getFieldClassName('origin', errors)} transition-colors`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.origin ? 'Pre-filled from previous leg' : 'Airport code'}
                  </p>
                </div>

                {/* Destination Airport */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To (Destination) *
                  </label>
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={handleInputChange}
                    placeholder="MIA"
                    maxLength="3"
                    className={`w-full px-3 py-2 border rounded-lg font-mono text-xl font-bold ${getFieldClassName('destination', errors)} transition-colors`}
                  />
                  {errors.destination && (
                    <p className="text-red-500 text-xs mt-1">{errors.destination}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Times Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-700 rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2">
                  🕐
                </span>
                Scheduled Times (24-hour format)
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Departure Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Departure (Out) *
                  </label>
                  <input
                    type="text"
                    name="scheduledOut"
                    value={formData.scheduledOut}
                    onChange={(e) => handleTimeInput(e, 'scheduledOut')}
                    placeholder="19:59"
                    maxLength="5"
                    className={`w-full px-3 py-2 border rounded-lg font-mono text-2xl font-bold text-center ${getFieldClassName('scheduledOut', errors)} transition-colors`}
                  />
                  {errors.scheduledOut && (
                    <p className="text-red-500 text-xs mt-1">{errors.scheduledOut}</p>
                  )}
                </div>

                {/* Arrival Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Arrival (In) *
                  </label>
                  <input
                    type="text"
                    name="scheduledIn"
                    value={formData.scheduledIn}
                    onChange={(e) => handleTimeInput(e, 'scheduledIn')}
                    placeholder="23:35"
                    maxLength="5"
                    className={`w-full px-3 py-2 border rounded-lg font-mono text-2xl font-bold text-center ${getFieldClassName('scheduledIn', errors)} transition-colors`}
                  />
                  {errors.scheduledIn && (
                    <p className="text-red-500 text-xs mt-1">{errors.scheduledIn}</p>
                  )}
                </div>
              </div>

              {/* Time sequence error */}
              {errors.timeSequence && (
                <p className="text-red-500 text-sm mt-2 flex items-center">
                  <span className="mr-2">⚠️</span>
                  {errors.timeSequence}
                </p>
              )}

              {/* Connection warning */}
              {connectionWarning && (
                <p className="text-orange-600 text-sm mt-2 flex items-center">
                  <span className="mr-2">⏱️</span>
                  {connectionWarning}
                </p>
              )}
            </div>

            {/* Block Time Preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Calculated Block Time:</span>
                <span className="text-2xl font-bold text-blue-600 font-mono">{blockTimeDisplay}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {formData.scheduledOut && formData.scheduledIn 
                  ? 'From departure to arrival (including next-day if applicable)'
                  : 'Enter both times to calculate'}
              </p>
            </div>

            {/* Before/After Comparison */}
            {flight && (
              <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-2">CURRENT</p>
                  <div className="space-y-1 text-sm text-gray-700">
                    <p>✈️ {flight.flight_number || 'N/A'}</p>
                    <p>📍 {flight.origin || flight.departure_airport || '?'} → {flight.destination || flight.arrival_airport || '?'}</p>
                    <p>🕐 {formatTimeForInput(flight.scheduled_out_time || flight.scheduledOut || '?')} - {formatTimeForInput(flight.scheduled_in_time || flight.scheduledIn || '?')}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-blue-600 mb-2">PROPOSED</p>
                  <div className="space-y-1 text-sm text-blue-700 font-semibold">
                    <p>✈️ {formData.flightNumber || 'N/A'}</p>
                    <p>📍 {formData.origin || '?'} → {formData.destination || '?'}</p>
                    <p>🕐 {formData.scheduledOut || '?'} - {formData.scheduledIn || '?'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer - Action Buttons */}
          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || showValidation && !Object.keys(errors).every(k => !errors[k])}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <span className="inline-block animate-spin">⟳</span>
                  Applying...
                </>
              ) : (
                '✓ Apply Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
